import { createPortal } from 'react-dom'

import { Modal } from '@/components/ui'

import type { UseMapControlsReturn } from './hooks/useMapControls'
import type { SaveableAssetMapContext } from './MapContext'

interface SaveMapModalProps extends UseMapControlsReturn {
  selectedSavedMap: SaveableAssetMapContext | null
}

export const SaveMapModal = ({
  showSaveModal,
  setShowSaveModal,
  saveMapName,
  setSaveMapName,
  saveMapIsDefault,
  setSaveMapIsDefault,
  saveError,
  setSaveError,
  handleConfirmSave,
  selectedSavedMap,
}: SaveMapModalProps) => {
  if (!showSaveModal) return null

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-base-100 rounded-lg shadow-xl p-6 w-96">
        <h3 className="text-lg font-medium mb-4">Save Current Map</h3>
        {selectedSavedMap && (
          <p className="text-sm text-base-content/70 mb-3">
            This will overwrite the current configuration. Enter a new name to save as a new map.
          </p>
        )}
        <input
          type="text"
          placeholder="Enter map name..."
          value={saveMapName}
          onChange={(e) => {
            setSaveMapName(e.target.value)
            setSaveError('')
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleConfirmSave()
            if (e.key === 'Escape') setShowSaveModal(false)
          }}
          className="input input-bordered w-full mb-2"
          autoFocus
        />
        {saveError && <p className="text-error text-sm mb-2">{saveError}</p>}
        <label className="flex items-center gap-2 cursor-pointer mt-2">
          <input
            type="checkbox"
            checked={saveMapIsDefault}
            onChange={(e) => setSaveMapIsDefault(e.target.checked)}
            className="checkbox checkbox-sm"
          />
          <span className="text-sm">Set as default</span>
        </label>
        {selectedSavedMap && (
          <p className="text-xs text-base-content/60 mt-1 ml-6">{selectedSavedMap.name} is the current default</p>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={() => setShowSaveModal(false)} className="btn btn-ghost">
            Cancel
          </button>
          <button onClick={handleConfirmSave} className="btn btn-primary">
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

interface DeleteMapModalProps extends UseMapControlsReturn {
  selectedSavedMap: SaveableAssetMapContext | null
}

export const DeleteMapModal = ({
  showDeleteModal,
  setShowDeleteModal,
  confirmDeleteSavedMap,
  selectedSavedMap,
}: DeleteMapModalProps) => {
  return (
    <Modal
      show={showDeleteModal}
      onHide={() => setShowDeleteModal(false)}
      title="Delete Saved Map"
      danger
      bodyContent={
        <p>
          Are you sure you want to delete <strong>{selectedSavedMap?.name}</strong>? This action cannot be undone.
        </p>
      }
      footerButtons={[
        { label: 'Delete', useModalVariant: true, onClick: confirmDeleteSavedMap },
        { label: 'Cancel', onClick: () => setShowDeleteModal(false) },
      ]}
    />
  )
}
